import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { savePermitForm, updatePermitForm } from '../../store/slices/projectPermitsSlice';
import { showToast } from '../utils/Toast';
import { fetchProjectData, prefillFormData } from '../../utils/projectDataUtils';
import { printPermitForm, createCompanyHeader, formatFormField, formatTableRow } from '../../utils/PermitPrintUtils';
import FloatingActionBar from './FloatingActionBar';

const FlashingsForm = ({ projectId, currentPermit, onClose }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  
  // Define all the form fields based on the image
  const [formData, setFormData] = useState({
    projectName: '',
    jobAddress: '',
    jobNumber: '',
    date: '',
    items: {
      pipeFlashings: { quantity: '', notes: '' },
      halfInch: { quantity: '', notes: '' },
      onePointFiveInch: { quantity: '', notes: '' },
      twoInch: { quantity: '', notes: '' },
      threeInch: { quantity: '', notes: '' },
      fourInch: { quantity: '', notes: '' },
      fiveInch: { quantity: '', notes: '' },
      eightInch: { quantity: '', notes: '' },
      tenInch: { quantity: '', notes: '' },
      smallTTop: { quantity: '', notes: '' },
      largeTTop: { quantity: '', notes: '' },
      tinShingles: { quantity: '', notes: '' },
      tilePan: { quantity: '', notes: '' },
      roofToWall: { quantity: '', notes: '' },
      zBar: { quantity: '', notes: '' },
      saddleStock: { quantity: '', notes: '' },
      counterFlashing: { quantity: '', notes: '' },
      valleyAndColor: { quantity: '', notes: '' },
      dripEdgeSizeAndColor: { quantity: '', notes: '' }
    }
  });

  useEffect(() => {
    if (currentPermit && currentPermit.formType === 'flashings') {
      setFormData(currentPermit.formData || formData);
    } else {
      // Fetch project data to prefill the form
      const getProjectData = async () => {
        try {
          const projectData = await fetchProjectData(projectId);
          const prefilled = prefillFormData(projectData, formData);
          setFormData(prefilled);
        } catch (error) {
          console.error('Error prefilling form data:', error);
        }
      };
      
      getProjectData();
    }
  }, [currentPermit, projectId]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleItemChange = (itemKey, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: {
        ...prev.items,
        [itemKey]: {
          ...prev.items[itemKey],
          [field]: value
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (currentPermit && currentPermit._id) {
        await dispatch(updatePermitForm({
          projectId,
          permitId: currentPermit._id,
          formData: {
            ...currentPermit,
            formData
          }
        })).unwrap();
        showToast('Flashings form updated successfully', 'success');
      } else {
        await dispatch(savePermitForm({
          projectId,
          formType: 'flashings',
          formData
        })).unwrap();
        showToast('Flashings form saved successfully', 'success');
      }
      onClose();
    } catch (error) {
      showToast(error.message || 'Failed to save Flashings form', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    // Create a printable version of the form content
    const formContent = `
      ${createCompanyHeader()}
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px;">
        <h1 class="form-title">Material List Flashings</h1>
        <div style="width: 200px;">
          ${formatFormField('Date', formData.date)}
          ${formatFormField('Job Number', formData.jobNumber)}
        </div>
      </div>
      
      <div class="form-section">
        ${formatFormField('Project Name', formData.projectName)}
        ${formatFormField('Job Address', formData.jobAddress)}
      </div>
      
      <div class="form-section">
        <table class="form-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Quantity</th>
            </tr>
          </thead>
          <tbody>
            ${Object.entries(formData.items).map(([key, value]) => `
              <tr>
                <td>${formatItemLabel(key)}</td>
                <td>${value.quantity || ''}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
    
    // Print the form
    printPermitForm(formData, 'Material List Flashings', formContent);
  };

  // Helper function to format camelCase keys into readable labels
  const formatItemLabel = (key) => {
    // Special cases
    if (key === 'pipeFlashings') return 'PIPE FLASHING(S)';
    if (key === 'halfInch') return '½"';
    if (key === 'onePointFiveInch') return '1.5"';
    if (key === 'twoInch') return '2"';
    if (key === 'threeInch') return '3"';
    if (key === 'fourInch') return '4"';
    if (key === 'fiveInch') return '5"';
    if (key === 'eightInch') return '8"';
    if (key === 'tenInch') return '10"';
    if (key === 'smallTTop') return 'Small T top';
    if (key === 'largeTTop') return 'Large T top';
    if (key === 'tinShingles') return 'Tin Shingles';
    if (key === 'tilePan') return 'Tile Pan';
    if (key === 'roofToWall') return 'Roof to wall';
    if (key === 'zBar') return 'Z bar';
    if (key === 'saddleStock') return 'Saddle Stock';
    if (key === 'counterFlashing') return 'Counter Flashing';
    if (key === 'valleyAndColor') return 'Valley and color (18" or 24")';
    if (key === 'dripEdgeSizeAndColor') return 'Drip edge size and color';
    
    // General case: convert camelCase to spaces and capitalize
    const withSpaces = key.replace(/([A-Z])/g, ' $1').trim();
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  };

  return (
    <div className="permit-form-shell bg-white p-6 rounded-lg max-w-5xl mx-auto relative">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <img 
            src="/logo/JRT-logo-small.jpg" 
            alt="JRT Roofing & Waterproofing" 
            className="h-16 mr-4"
          />
          <div>
            <h2 className="text-2xl font-bold">JRT ROOFING & WATERPROOFING</h2>
            <p className="text-sm">QLTY CRAFTSMANSHIP</p>
          </div>
        </div>
        <div className="text-right">
          <p>P.O. Box 3688</p>
          <p>San Clemente, CA 92672</p>
          <p>Phone: (949) 742-2892</p>
          <p className="uppercase">JRTROOFING.COM</p>
          <p>CA 1070037</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="col-span-1">
          <h1 className="text-2xl font-bold">Material List Flashings</h1>
        </div>
        <div className="col-span-1 grid grid-cols-2 gap-2">
          <div className="text-right pr-2">
            <div className="text-sm font-medium">Date:</div>
          </div>
          <div>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => handleChange('date', e.target.value)}
              className="w-full border border-gray-300 p-1 rounded"
            />
          </div>
          <div className="text-right pr-2">
            <div className="text-sm font-medium">Job Number:</div>
          </div>
          <div>
            <input
              type="text"
              value={formData.jobNumber}
              onChange={(e) => handleChange('jobNumber', e.target.value)}
              className="w-full border border-gray-300 p-1 rounded"
            />
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name:</label>
            <input
              type="text"
              value={formData.projectName}
              onChange={(e) => handleChange('projectName', e.target.value)}
              className="w-full border border-gray-300 p-2 rounded"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Address:</label>
            <input
              type="text"
              value={formData.jobAddress}
              onChange={(e) => handleChange('jobAddress', e.target.value)}
              className="w-full border border-gray-300 p-2 rounded"
            />
          </div>
        </div>

        <div className="overflow-x-auto mb-6">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr>
                <th className="border border-gray-300 p-2 w-1/2 text-left">Item</th>
                <th className="border border-gray-300 p-2 w-1/2 text-center">Quantity</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(formData.items).map(([key, value]) => (
                <tr key={key}>
                  <td className="border border-gray-300 p-2 font-medium">
                    {formatItemLabel(key)}
                  </td>
                  <td className="border border-gray-300 p-2">
                    <input
                      type="text"
                      value={value.quantity}
                      onChange={(e) => handleItemChange(key, 'quantity', e.target.value)}
                      className="w-full p-1 border border-gray-200 rounded"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>


        {/* Add padding at the bottom to ensure content isn't hidden behind the floating bar */}
        <div className="pb-20"></div>
      </form>
      
      {/* Floating Action Bar */}
      <FloatingActionBar 
        onCancel={onClose}
        onDownload={handleDownload}
        onSubmit={handleSubmit}
        loading={loading}
        isUpdate={!!(currentPermit && currentPermit._id)}
      />
    </div>
  );
};

export default FlashingsForm;
